import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { getApiBaseUrl } from "../../config/api";
import { currentSalaryPeriod, formatSalaryPeriodLabel } from "../../utils/salaryPeriod";
import { downloadPayslipPdf, fetchTeacherHrSummary } from "../../utils/teacherHrApi";

const formatMWK = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "—";
    return "MWK " + x.toLocaleString();
};

function statusChip(status) {
    const s = String(status || "Unpaid");
    if (s === "Paid") return { label: "Paid in full", color: "success" };
    if (s === "Pending") return { label: "Payment processing", color: "warning" };
    return { label: "Unpaid", color: "default" };
}

function formatDate(d) {
    if (!d) return "—";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return "—";
    return x.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const TeacherSalary = () => {
    const { currentUser } = useSelector((s) => s.user);
    const [detail, setDetail] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [payslips, setPayslips] = useState([]);

    const teacherId = currentUser?._id || currentUser?.id;
    const viewPeriod = currentSalaryPeriod();
    const periodLabel =
        detail?.salaryCurrentPeriodLabel ||
        currentUser?.salaryCurrentPeriodLabel ||
        formatSalaryPeriodLabel(viewPeriod);

    const load = useCallback(async () => {
        if (!teacherId) return;
        setLoadError("");
        try {
            const res = await axios.get(`${getApiBaseUrl()}/Teacher/${teacherId}`);
            if (res.data?.message && !res.data?._id && !res.data?.id) {
                setLoadError(res.data.message);
                return;
            }
            setDetail(res.data);
            try {
                const hr = await fetchTeacherHrSummary(teacherId);
                setPayslips(hr?.teacher?.payslips || []);
            } catch {
                setPayslips([]);
            }
        } catch (err) {
            setLoadError(err.response?.data?.message || err.message || "Could not load salary details");
        }
    }, [teacherId]);

    useEffect(() => {
        load();
    }, [load]);

    const salaryAmount = detail?.salaryAmount ?? currentUser?.salaryAmount;
    const status = detail?.salaryPaymentStatus ?? currentUser?.salaryPaymentStatus ?? "Unpaid";
    const chip = statusChip(status);
    const balanceDue = status === "Paid" ? 0 : Number(salaryAmount) > 0 ? Number(salaryAmount) : null;

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <PaymentsOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        My salary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Monthly payroll for {periodLabel}
                    </Typography>
                </Box>
            </Stack>

            {loadError ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            {!salaryAmount && salaryAmount !== 0 ? (
                <Alert severity="info">
                    Your monthly salary has not been set yet. Contact the school administrator if you have questions.
                </Alert>
            ) : (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={3}>
                        <Box textAlign="center">
                            <Typography variant="overline" color="text.secondary">
                                Monthly salary ({periodLabel})
                            </Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
                                {formatMWK(salaryAmount)}
                            </Typography>
                            <Chip label={chip.label} color={chip.color} sx={{ mt: 2 }} />
                        </Box>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="center"
                        >
                            <Box textAlign="center" flex={1}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Balance due this month
                                </Typography>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    color={balanceDue === 0 ? "success.main" : "text.primary"}
                                >
                                    {balanceDue === 0 ? formatMWK(0) : balanceDue != null ? formatMWK(balanceDue) : "—"}
                                </Typography>
                            </Box>
                            <Box textAlign="center" flex={1}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Last paid month
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {detail?.salaryLastPaidPeriodLabel ||
                                        (detail?.salaryLastPaidPeriod
                                            ? formatSalaryPeriodLabel(detail.salaryLastPaidPeriod)
                                            : "—")}
                                </Typography>
                            </Box>
                            <Box textAlign="center" flex={1}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Date received
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {formatDate(detail?.salaryLastPaidAt ?? currentUser?.salaryLastPaidAt)}
                                </Typography>
                            </Box>
                        </Stack>

                        {status === "Pending" ? (
                            <Alert severity="info">
                                Your salary for {periodLabel} has been submitted and is awaiting school approval.
                            </Alert>
                        ) : null}
                        {status === "Paid" ? (
                            <Alert severity="success">
                                {periodLabel} salary paid in full ({formatMWK(salaryAmount)}). Your balance for this month
                                is zero.
                            </Alert>
                        ) : status === "Unpaid" && Number(salaryAmount) > 0 ? (
                            <Alert severity="warning">
                                Your {periodLabel} salary of {formatMWK(salaryAmount)} is still outstanding. It will update
                                here when the school records and approves your monthly payment.
                            </Alert>
                        ) : null}
                    </Stack>
                </Paper>
            )}

            {payslips.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Payslips
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Download official payslips generated by the school administrator.
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Period</TableCell>
                                <TableCell align="right">Net pay</TableCell>
                                <TableCell align="right">PDF</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payslips.map((p) => (
                                <TableRow key={p._id || p.id}>
                                    <TableCell>{p.period}</TableCell>
                                    <TableCell align="right">
                                        {Number(p.netPay || 0).toLocaleString(undefined, {
                                            style: "currency",
                                            currency: "MWK",
                                            minimumFractionDigits: 0,
                                        })}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => downloadPayslipPdf(p._id || p.id)}
                                        >
                                            Download
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            ) : null}
        </Container>
    );
};

export default TeacherSalary;
